import type { Linework } from "building-types/annotation/annotationTypes";
import {
  PathEndStyle,
  PlacementXAnchor,
  PlacementYAnchor,
  PositioningMethod,
  TextAlignment,
  TextVerticalAlignment,
} from "building-types/annotation/annotationTypesEnum";
import type { DXFContentInfo, DXFUnit } from "building-types/assets/dxf";
import type {
  SheetArcGeometry,
  SheetCircleGeometry,
  SheetGeometry,
  SheetGeometryList,
  SheetPolylineGeometry,
} from "building-types/views/sheetGeometryTypes";
import {
  isSheetTextGeometry,
  sheetArcGeometryNew,
  sheetCircleGeometryNew,
  sheetGeometryListNew,
  sheetPolylineGeometryNew,
  sheetTextGeometryNew,
} from "building-types/views/sheetGeometryTypes";
import { ONE_INCH, ZERO_INCHES, ZERO_POINTS } from "common/constants/scaleFactors";
import { assertUnreachable } from "common/utils/assertUnreachable";
import degreeToRadian from "common/utils/degreeToRadian";
import { oneHundredthMillimetersNew, numMul, percentNew, sheetInchesNew } from "common/utils/nominalNumbers";
import type { Mutable } from "common/utils/types";
import {
  DXF_UNIT_CODES,
  DXF_MTEXT_ANCHORS,
  DXF_PLACEMENT_X_ANCHOR,
  DXF_PLACEMENT_Y_ANCHOR,
  DXF_HATCH_PATTERNS,
} from "doc/plans/dxfConstants";
import type { DXFContent, DXFStyle, DXFStyleSheet } from "doc/plans/dxfParserTypes";
import { planAddAnnotation } from "doc/plans/planAnnotations";
import { styleSheetNew } from "doc/plans/planStyles";
import sheetGeometryListAABB2 from "doc/plans/sheetGeometryAABB2";
import {
  isUniformScale,
  mat3TransformWithComponents,
  mat3TransformWithComponentsUnchecked,
  sheetGeometryTransform,
} from "doc/plans/sheetGeometryTools";
import { linearDimensionNew, lineworkNew, noteNew } from "doc/utils/annotation";
import type {
  IArcEntity,
  IBlock,
  ICircleEntity,
  IDxf,
  IEllipseEntity,
  IEntity,
  IInsertEntity,
  ILineEntity,
  ILineType,
  IMtextEntity,
  IPoint,
  IPolylineEntity,
  ITextEntity,
} from "dxf-parser";
import DxfParser from "dxf-parser";
import type { EntityName as DXFParserEntityTypes } from "dxf-parser/dist/entities/geomtry";
import { aabb2Move } from "geom/aabb2";
import { aabb2New } from "geom/aabb2Types";
import type { AABB2 } from "geom/aabb2Types";
import { bulgeArcIntermediatePoints } from "geom/arc";
import { ANGLE_EPSILON, DIST_EPSILON_SQUARED, TWO_PI } from "geom/core";
import { line2Tangent } from "geom/line2";
import { mat3Ident, mat3Mult, mat3Rotate, mat3Scale, mat3Translate } from "geom/mat3";
import { polyLine2Close, polyLine2IsClosed } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { polyLine2New } from "geom/polyLine2Types";
import { vec2Add, vec2DistSquared, vec2Dot, vec2Sub } from "geom/vec2";
import type { IAttribEntity } from "./dxfParserLibExtensions/Attrib";
import Attrib from "./dxfParserLibExtensions/Attrib";
import type { IDimensionEntity } from "./dxfParserLibExtensions/Dimension";
import Dimension from "./dxfParserLibExtensions/Dimension";
import type { IHatchEntity } from "./dxfParserLibExtensions/Hatch";
import Hatch from "./dxfParserLibExtensions/Hatch";
import type { ILeaderEntity } from "./dxfParserLibExtensions/Leader";
import Leader from "./dxfParserLibExtensions/Leader";
import type { ILwpolylineEntity } from "./dxfParserLibExtensions/Lwpolyline";
import Lwpolyline from "./dxfParserLibExtensions/Lwpolyline";
import type { IMLeaderEntity } from "./dxfParserLibExtensions/MLeader";
import MLeader, { MultiLeader } from "./dxfParserLibExtensions/MLeader";
import type { ISolidEntity } from "./dxfParserLibExtensions/Solid";
import Solid from "./dxfParserLibExtensions/Solid";
import { unitToInchFactor } from "./dxfUnitConversion";
import type { DXFDimHeaderDefaults, DXFDimStyle, DXFDimStyleOverrides } from "./parseDXFDimStyle";
import { parseDimHeaderDefaults, parseDimStyles, resolveNormalizedDimSize } from "./parseDXFDimStyle";
import { parseDXFTextOverrides, parseDXFTextRTFOverrides } from "./parseDXFText";

export type DxfImportIssueKind = "NON_UNIFORM_SCALE";

export interface DxfImportIssue {
  readonly kind: DxfImportIssueKind;
  readonly objectName: string;
  readonly layerName: string;
}

export interface DXFContentPreview extends DXFContentInfo {
  readonly importIssues: readonly DxfImportIssue[];
}

export const DXF_NON_UNIFORM_SCALE_BANNER_MESSAGE =
  "Non-uniform scaling is not currently supported. See the Log for more details.";

export function dxfImportIssueLogMessage(issue: DxfImportIssue): string {
  switch (issue.kind) {
    case "NON_UNIFORM_SCALE": {
      return `${issue.objectName} in Layer ${issue.layerName} has a non-uniform scale. Non-uniform scaling is not currently supported, which may result in incorrect geometry.`;
    }
    default: {
      assertUnreachable(issue.kind);
    }
  }
}

interface PendingInsertScan {
  name?: string;
  layer?: string;
  handle?: string;
  xScale: number;
  yScale: number;
}

function pendingInsertToIssue(insert: PendingInsertScan): DxfImportIssue | null {
  if (isUniformScale(insert.xScale, insert.yScale)) {
    return null;
  }
  return {
    kind: "NON_UNIFORM_SCALE",
    objectName: insert.name || insert.handle || "INSERT",
    layerName: insert.layer || "0",
  };
}

/**
 * @param dxfContentString
 * @returns minimal information about the dxf to be stored on entities, plus import issues
 */
export function parseDXFContentInfo(dxfContentString: string): DXFContentPreview {
  let geometryUnit: DXFUnit = "Unitless";
  let geometryAABB2: AABB2 | null = null;
  const geometryLayers = new Set<string>();
  const importIssues: DxfImportIssue[] = [];

  let extMin: number[] | null = null;
  let extMax: number[] | null = null;

  const lines = dxfContentString.split(/\r?\n/);

  let currentEntity: string | null = null;
  let pendingInsert: PendingInsertScan | null = null;

  const flushPendingInsert = () => {
    if (!pendingInsert) {
      return;
    }
    const issue = pendingInsertToIssue(pendingInsert);
    if (issue) {
      importIssues.push(issue);
    }
    pendingInsert = null;
  };

  for (let i = 0; i < lines.length - 1; i += 2) {
    const code = lines[i].trim();
    const value = lines[i + 1]?.trim() ?? "";

    // i is group 9; i+1 is the header variable. Later pairs are peeked: i+2/i+3, i+4/i+5, …
    if (code === "9") {
      if (value === "$INSUNITS") {
        const unitCodeLine = lines[i + 2]?.trim(); // group 70
        const unitValueLine = lines[i + 3]?.trim(); // unit enum
        if (unitCodeLine === "70" && unitValueLine) {
          const unitCode = parseInt(unitValueLine, 10);
          geometryUnit = DXF_UNIT_CODES[unitCode] || "Unitless";
        }
      } else if (value === "$EXTMIN") {
        const xLine = lines[i + 3]?.trim(); // X (group 10)
        const yLine = lines[i + 5]?.trim(); // Y (group 20)
        if (xLine && yLine) {
          extMin = [parseFloat(xLine), parseFloat(yLine)];
        }
      } else if (value === "$EXTMAX") {
        const xLine = lines[i + 3]?.trim(); // X (group 10)
        const yLine = lines[i + 5]?.trim(); // Y (group 20)
        if (xLine && yLine) {
          extMax = [parseFloat(xLine), parseFloat(yLine)];
        }
      }
    }

    if (code === "0") {
      // Entity type
      flushPendingInsert();
      currentEntity = value;
      if (value === "INSERT") {
        pendingInsert = { xScale: 1, yScale: 1 };
      }
      continue;
    }

    if (currentEntity === "LAYER" && code === "2" && value) {
      geometryLayers.add(value);
    }

    if (pendingInsert) {
      if (code === "2") {
        // Block name
        pendingInsert.name = value;
      } else if (code === "8") {
        // Layer name
        pendingInsert.layer = value;
      } else if (code === "5") {
        // Entity handle
        pendingInsert.handle = value;
      } else if (code === "41") {
        // X scale
        const xScale = parseFloat(value);
        pendingInsert.xScale = Number.isNaN(xScale) ? 1 : xScale;
      } else if (code === "42") {
        // Y scale
        const yScale = parseFloat(value);
        pendingInsert.yScale = Number.isNaN(yScale) ? 1 : yScale;
      }
    }
  }
  flushPendingInsert();

  if (extMin && extMax) {
    const [minX, minY] = extMin;
    const [maxX, maxY] = extMax;
    if (minX <= maxX && minY <= maxY) {
      const inchFactor = unitToInchFactor(geometryUnit);
      if (inchFactor !== null) {
        // Convert the file's native bounds to inches so preview size matches a full parse.
        geometryAABB2 = aabb2New(minX * inchFactor, minY * inchFactor, maxX * inchFactor, maxY * inchFactor);
        geometryAABB2 = aabb2Move(geometryAABB2, -geometryAABB2.minX, -geometryAABB2.minY);
        geometryUnit = "Inches";
      }
    }
  }

  if (!geometryAABB2) {
    // Fallback to full parse if extents are not found
    const parsed = parseDXFContent(dxfContentString);
    geometryAABB2 = parsed.geometryAABB2;
    geometryUnit = parsed.geometryUnit;
    geometryLayers.clear();
    for (const layer of parsed.geometryLayers) {
      geometryLayers.add(layer);
    }
  }

  return {
    geometryLayers: [...geometryLayers],
    geometryAABB2,
    geometryUnit,
    importIssues,
  };
}

/**
 * This is a wrapper around the DXF content that is used to add additional
 * data that is not handled by dxfParser.
 * If we instead decide to fork dxfParser and add the missing content,
 * we can remove this wrapper.
 */
interface ExtendedDxf {
  /**
   * The original DXF content
   */
  dxf: IDxf;
  /**
   * A map of layer names to their line types
   * @NOTE - dxfParser does not accurately extract this information.
   */
  dxfLayerLineTypes: { [layerName: string]: string };
  /**
   * DIMSTYLE table records (name → DIMSCALE / DIMASZ / DIMTXT)
   * @NOTE - dxfParser does not parse the DIMSTYLE table.
   */
  dimStyles: { [name: string]: DXFDimStyle };
  /**
   * HEADER dimension defaults ($DIMSTYLE, $DIMSCALE, $DIMASZ, $DIMTXT)
   */
  dimHeaderDefaults: DXFDimHeaderDefaults;
}

/**
 * Parses layer line types (code 6) from DXF content string.
 * @param dxfContentString The raw DXF file content
 * @returns Map of layer names to their line types
 *
 * @NOTE - dxfParser does not accurately extract this information.
 * @link https://github.com/gdsestimating/dxf-parser/blob/0df7a37a4207a1f925b8d0bfffc270ff121446b4/src/DxfParser.ts#L697
 */
function parseLayerLineTypes(dxf: string): ExtendedDxf["dxfLayerLineTypes"] {
  const layers: { [layerName: string]: string } = {};
  const lines = dxf.split(/\r?\n/);

  let isLayerTable = false;
  let currentLayer: string | null = null;
  let currentLineType: string | null = null;

  for (let i = 0; i < lines.length - 1; i += 2) {
    const code = lines[i].trim();
    const value = lines[i + 1]?.trim();

    if (code === "0" && value === "TABLE") {
      const nextCode = lines[i + 2]?.trim();
      const nextValue = lines[i + 3]?.trim();
      if (nextCode === "2" && nextValue === "LAYER") {
        isLayerTable = true;
      }
    }

    if (isLayerTable) {
      if (code === "0" && value === "ENDTAB") {
        break; // Exit LAYER table
      }

      if (code === "0" && value === "LAYER") {
        currentLayer = null;
        currentLineType = null;
      }

      if (code === "2") {
        currentLayer = value;
      } else if (code === "6") {
        currentLineType = value;
      }

      if (currentLayer && currentLineType) {
        layers[currentLayer] = currentLineType;
        currentLayer = null;
        currentLineType = null;
      }
    }
  }

  return layers;
}

const EMPTY_DXF_CONTENT: DXFContent = {
  geometryLayers: [],
  geometryUnit: "Inches",
  geometryAABB2: aabb2New(0, 0, 0, 0),
  geometryStyles: {},
  geometry: {},
};

/**
 * Main Parsing function for DXF content
 * This function will not scale any content, and will return the geometry
 * and styling metadata in it's original units.
 * @param dxfContentString
 * @returns
 */
export function parseDXFContent(dxfContentString: string): DXFContent {
  const dxfParser = new DxfParser();

  // add all our own internal handlers here
  dxfParser.registerEntityHandler(Dimension);
  dxfParser.registerEntityHandler(Leader);
  dxfParser.registerEntityHandler(MLeader);
  dxfParser.registerEntityHandler(MultiLeader);
  dxfParser.registerEntityHandler(Hatch);
  dxfParser.registerEntityHandler(Attrib);
  dxfParser.registerEntityHandler(Solid);
  dxfParser.registerEntityHandler(Lwpolyline);

  const dxf = dxfParser.parse(dxfContentString);
  if (!dxf) {
    return EMPTY_DXF_CONTENT;
  }

  const dxfUnit = parseUnits(dxf);
  const inchFactor = unitToInchFactor(dxfUnit);
  if (inchFactor === null) {
    console.error(`Unsupported DXF Unit: ${dxfUnit}`);
    return EMPTY_DXF_CONTENT;
  }

  // Add data that is not handled by dxfParser
  const dxfWithExtendedData: ExtendedDxf = {
    dxf,
    dxfLayerLineTypes: parseLayerLineTypes(dxfContentString),
    dimStyles: parseDimStyles(dxfContentString),
    dimHeaderDefaults: parseDimHeaderDefaults(dxfContentString),
  };

  const geometryStyles = scaleDxfStyleSheet(parseStyleSheet(dxfWithExtendedData), inchFactor);
  const geometry = parseEntities(dxfWithExtendedData);

  let geometryAABB2 = parseAABB(dxf);
  if (geometryAABB2 === null) {
    const geometryArray = Object.values(geometry).flatMap((g) => g.geometry);
    // some dxfs don't have header information about the AABB of the
    // content. In this case, we need to compute it from the geometry.
    geometryAABB2 = sheetGeometryListAABB2({
      geometry: geometryArray,
    });
  }

  // scale coordinates to inches
  geometryAABB2 = aabb2New(
    geometryAABB2.minX * inchFactor,
    geometryAABB2.minY * inchFactor,
    geometryAABB2.maxX * inchFactor,
    geometryAABB2.maxY * inchFactor,
  );
  // zero out the tranformation of the content to the origin
  const dxfTransform = mat3Mult(
    mat3Translate(-geometryAABB2.minX, -geometryAABB2.minY),
    mat3Scale(inchFactor, inchFactor),
  );
  const dxfTransformWithComponents = mat3TransformWithComponents(dxfTransform);
  geometryAABB2 = aabb2Move(geometryAABB2, -geometryAABB2.minX, -geometryAABB2.minY);

  return {
    geometryLayers: parseLayers(dxf),
    geometryUnit: "Inches", // all geometry is converted to inches
    geometryAABB2,
    geometryStyles: geometryStyles,
    geometry: Object.keys(geometry).reduce(
      (acc, layer) => {
        const layerGeometry = geometry[layer];
        return {
          ...acc,
          [layer]: {
            geometry: layerGeometry.geometry.map((g) => {
              // in order for this geometry to be styled in the future, we need to
              // ensure that there are no undefined values in the object
              const stripped = stripUndefinedValues(g);
              // place all geomety relative to the origin
              const transformed = sheetGeometryTransform(stripped, dxfTransformWithComponents);
              return scaleSheetTextSizes(transformed, inchFactor);
            }),
          } satisfies SheetGeometryList,
        };
      },
      {} as { [layer: string]: SheetGeometryList },
    ),
  };
}

// this is a bit of a hack, but we don't have a good override mechanism
// yet for ensuring these don't cascade
function stripUndefinedValues<T extends Object>(o: T): T {
  for (const k in o) {
    if (o[k] === undefined) {
      delete o[k];
    }
  }
  return o;
}

function parseLayers(dxf: IDxf): string[] {
  const layers = dxf.tables.layer.layers;
  return Object.keys(layers);
}

function parseAABB(dxf: IDxf): AABB2 | null {
  if (!dxf.header?.$EXTMIN || !dxf.header?.$EXTMAX) {
    return null;
  }
  const minX = (dxf.header.$EXTMIN as IPoint).x ?? 0;
  const minY = (dxf.header.$EXTMIN as IPoint).y ?? 0;
  const maxX = (dxf.header.$EXTMAX as IPoint).x ?? 0;
  const maxY = (dxf.header.$EXTMAX as IPoint).y ?? 0;

  if (minX > maxX || minY > maxY) {
    return null; // DXF has no initialized bounds
  }

  return aabb2New(minX, minY, maxX, maxY);
}

function parseUnits(dxf: IDxf): DXFUnit {
  let unit = DXF_UNIT_CODES[0];
  if (typeof dxf.header.$INSUNITS === "number") {
    unit = DXF_UNIT_CODES[dxf.header.$INSUNITS];
  } else {
    if (!(`unknown DXF unit, defaulting to ${unit}`)) {
      console.assert(false);
    }
  }
  return unit;
}

const HIDDEN_TAG = "__HIGHARC_HIDDEN__";

function parseStyleSheet(dxf: ExtendedDxf): DXFStyleSheet {
  const { tables, entities, blocks } = dxf.dxf;

  const styles: Mutable<DXFStyleSheet> = {
    [HIDDEN_TAG]: {
      hidden: true,
    },
  };

  /**
   * In the event that entities have inline styles, we need to
   * add a new style for this entity to the style sheet.
   * If this becomes a performance burden (i.e there are too many individual style overrides),
   * we should hash the style and add a unique tag for the style hash rather than the
   * entity handle.
   */
  const addEntityStyle = (entity: IEntity) => {
    const { lineTypeScale, lineweight } = entity;
    const style: Mutable<DXFStyle> = {};
    let addStyle = false;
    if (lineTypeScale !== undefined) {
      style.lineTypeScale = lineTypeScale;
      addStyle = true;
    }
    if (lineweight !== undefined && lineweight > 0) {
      style.lineWeight = oneHundredthMillimetersNew(lineweight);
      addStyle = true;
    }
    if (addStyle) {
      styles[parseEntityHandle(entity)] = style;
    }
  };

  /**
   * Adds the normal DXF lineTypes to the style sheet.
   */
  const addLineTypeStyle = (lineType: ILineType) => {
    const lineTypeName = lineType.name;
    if (isInternalLineType(lineTypeName)) {
      return;
    }
    if (lineType.patternLength === 0) {
      styles[lineType.name] = {};
    } else {
      const pattern = lineType.pattern.map((i) => Math.abs(parseFloat(i)));
      styles[lineType.name] = {
        patternLength: lineType.patternLength,
        pattern,
      };
    }
  };

  if (tables && tables.lineType) {
    for (const lineType in tables.lineType.lineTypes) {
      addLineTypeStyle(tables.lineType.lineTypes[lineType]);
    }
  }

  for (const entity of entities) {
    addEntityStyle(entity);
  }

  /**
   * TODO - we should probably parse the block lineTypes the same way we do the layer
   * lineTypes, and add them to the ExtendedDxf object. (dxf-parser does not parse block lineTypes)
   * If we do that, we would need to check entities for their block and add the
   * appropriate lineType tag to the entity.
   */
  for (const blockId in blocks) {
    const block = blocks[blockId];
    if (!block.entities) {
      continue;
    }
    for (const entity of block.entities) {
      addEntityStyle(entity);
    }
  }

  return styleSheetNew(styles);
}

function parseEntities(dxf: ExtendedDxf): { [layer: string]: SheetGeometryList } {
  const { entities } = dxf.dxf;
  const geometry: { [layer: string]: SheetGeometryList } = {};

  if (!entities) {
    return geometry;
  }

  for (const entity of entities) {
    if (!geometry[entity.layer]) {
      geometry[entity.layer] = sheetGeometryListNew();
    }
    geometry[entity.layer].geometry.push(...parseEntity(entity, dxf));
  }

  return geometry;
}

function parseBlock(block: IBlock, dxf: ExtendedDxf) {
  const { position, entities } = block;
  const { x, y } = position;

  if (!entities || !entities.length) {
    return { parsedEntities: [], blockTransform: mat3Ident() };
  }

  const blockTransform = mat3Translate(x, y);
  const parsedEntities = parseEntityArray(entities, dxf);
  return { parsedEntities, blockTransform };
}

function parseInsertEntity(entity: IInsertEntity, dxf: ExtendedDxf) {
  const { position, rotation = 0, xScale = 1, yScale = 1 } = entity;
  const { x, y } = position;

  const block = dxf.dxf.blocks[entity.name];
  if (!block) {
    return null;
  }

  const insertRotation = degreeToRadian(rotation);
  const insertTransform = mat3Mult(
    mat3Mult(mat3Translate(x, y), mat3Rotate(insertRotation)),
    mat3Scale(xScale, yScale),
  );
  const { parsedEntities, blockTransform } = parseBlock(block, dxf);
  const transformWithComponents = mat3TransformWithComponentsUnchecked(mat3Mult(insertTransform, blockTransform));
  const textScale = Math.abs(xScale);
  return parsedEntities.map((g) => {
    const transformed = sheetGeometryTransform(g, transformWithComponents);
    return scaleSheetTextSizes(transformed, textScale);
  });
}

function scaleSheetTextSizes(geom: SheetGeometry, scale: number): SheetGeometry {
  // sheetGeometryTransform only moves/rotates TEXT; sizes stay in source units until this call.
  if (!isSheetTextGeometry(geom) || scale === 1) {
    return geom;
  }
  return {
    ...geom,
    fontSize: geom.fontSize !== undefined ? numMul(geom.fontSize, scale) : undefined,
    boxWidth: geom.boxWidth !== undefined ? numMul(geom.boxWidth, scale) : undefined,
    boxHeight: geom.boxHeight !== undefined ? numMul(geom.boxHeight, scale) : undefined,
  };
}

function scaleDxfStyleSheet(styles: DXFStyleSheet, inchFactor: number): DXFStyleSheet {
  if (inchFactor === 1) {
    return styles;
  }
  const scaled: Mutable<DXFStyleSheet> = {};
  for (const tag of Object.keys(styles)) {
    const style = styles[tag];
    scaled[tag] = {
      ...style,
      ...(style.patternLength !== undefined ? { patternLength: style.patternLength * inchFactor } : {}),
      ...(style.pattern !== undefined ? { pattern: style.pattern.map((dash) => dash * inchFactor) } : {}),
    };
  }
  return scaled;
}

function parseEntityArray(entities: IEntity[], dxf: ExtendedDxf) {
  const geometry: SheetGeometry[] = [];
  for (const entity of entities) {
    geometry.push(...parseEntity(entity, dxf));
  }
  return geometry;
}

export function parseEntity(entity: IEntity, dxf: ExtendedDxf): SheetGeometry[] {
  const geometry: SheetGeometry[] = [];

  // always add the layer tag
  // this allows us to apply user style overrides by layer
  const geometryTags = [entity.layer];

  if (entity.lineType !== undefined) {
    const toUpperCase = entity.lineType.toUpperCase();
    switch (toUpperCase) {
      case "BYLAYER": {
        const layer = entity.layer;
        const lineType = dxf.dxfLayerLineTypes[layer];
        if (lineType) {
          geometryTags.push(lineType);
        }
        break;
      }
      case "BYBLOCK": {
        // not supported yet
        break;
      }
      case "CONTINUOUS": {
        // default line type
        break;
      }
      default: {
        geometryTags.push(entity.lineType);
      }
    }
  } else {
    // undefined linetype = BYLAYER
    // undefined layer linetype = CONTINUOUS
    const layer = entity.layer;
    const lineType = dxf.dxfLayerLineTypes[layer];
    if (lineType) {
      geometryTags.push(lineType);
    }
  }

  // Add handle tag to identify the entity and map to any instance styles
  geometryTags.push(parseEntityHandle(entity));

  // Add hidden tag if entity is hidden - this precents us from needing to
  // create a new style tag for each hidden entity
  if (entity.visible !== undefined && !entity.visible) {
    geometryTags.push(HIDDEN_TAG);
  }

  const parsedEntity = parseEntityInternal(entity, dxf);
  if (parsedEntity) {
    if (Array.isArray(parsedEntity)) {
      geometry.push(...parsedEntity);
    } else {
      geometry.push(parsedEntity);
    }
  }

  return geometry.map((g) => {
    const tags = g.tags ?? [];
    // insert parent tags under entity tags
    tags.splice(0, 0, ...geometryTags);
    return {
      ...g,
      tags,
    };
  });
}

function parseEntityHandle(entity: IEntity) {
  return `${entity.type}.${entity.handle}`;
}

type DXFInternalEntityTypes = "LEADER" | "MLEADER" | "MULTILEADER" | "HATCH" | "ATTRIB";
type DXFParsableEntityTypes = DXFParserEntityTypes | DXFInternalEntityTypes;

function parseEntityInternal(entity: IEntity, dxf: ExtendedDxf): SheetGeometry | SheetGeometry[] | null {
  const entityType = entity.type as DXFParsableEntityTypes;
  switch (entityType) {
    case "LINE": {
      return parseLineworkEntity(entity as ILineEntity);
    }
    case "LWPOLYLINE": {
      return parseLineworkEntity(entity as ILwpolylineEntity);
    }
    case "POLYLINE": {
      return parseLineworkEntity(entity as IPolylineEntity);
    }
    case "ARC": {
      return parseArcEntity(entity as IArcEntity);
    }
    case "CIRCLE": {
      return parseCircleEntity(entity as ICircleEntity);
    }
    case "ELLIPSE": {
      return parseEllipseEntity(entity as IEllipseEntity);
    }
    case "LEADER": {
      return parseLeaderEntity(entity as ILeaderEntity, dxf);
    }
    case "MLEADER":
    case "MULTILEADER": {
      return parseMLeaderEntity(entity as IMLeaderEntity, dxf);
    }
    case "TEXT": {
      return parseTextEntity(entity as ITextEntity);
    }
    case "MTEXT": {
      return parseMTextEntity(entity as IMtextEntity);
    }
    case "DIMENSION": {
      return parseDimensionEntity(entity as IDimensionEntity, dxf);
    }
    case "INSERT": {
      return parseInsertEntity(entity as IInsertEntity, dxf);
    }
    case "HATCH": {
      return parseHatchEntity(entity as IHatchEntity);
    }
    case "ATTRIB": {
      return parseAttribEntity(entity as IAttribEntity);
    }
    case "SOLID": {
      return parseSolidEntity(entity as ISolidEntity);
    }
    case "ATTDEF":
    case "POINT":
    case "3DFACE":
    case "SPLINE":
    case "VERTEX": {
      /**
       * @TODO - currently unhandled entity types
       */
      return null;
    }
    default: {
      assertUnreachable(entityType);
      // return null;
    }
  }
}

/**
 * DXF DIMENSION group 70 type enum (values 0–6). Bits 32 / 64 / 128 are separate flags added to these.
 * Bit 32 = exclusive anonymous block ref (always set in R13+), not annotative.
 * Annotative is separate (AcadAnnotative xdata / extension dict) and is not read here.
 */
const DXF_DIMENSION_TYPE = {
  ROTATED_HORIZONTAL_VERTICAL: 0,
  ALIGNED: 1,
  ANGULAR: 2,
  DIAMETER: 3,
  RADIUS: 4,
  ANGULAR_3_POINT: 5,
  ORDINATE: 6,
};

/** Mask for group 70 type enum values 0–6 (low 3 bits). */
const DXF_DIMENSION_TYPE_MASK = 7;

interface DimensionTypeInfo {
  dimensionType: number;
  hasExclusiveBlockRef: boolean;
  isOrdinateXType: boolean;
  hasUserLocatedText: boolean;
}

function parseDimensionType(flags: number): DimensionTypeInfo {
  return {
    dimensionType: flags & DXF_DIMENSION_TYPE_MASK,
    hasExclusiveBlockRef: (flags & 32) !== 0,
    isOrdinateXType: (flags & 64) !== 0,
    hasUserLocatedText: (flags & 128) !== 0,
  };
}

function parseDimensionBlock(entity: IDimensionEntity, dxf: ExtendedDxf): SheetGeometry[] | null {
  if (!entity.block) {
    return null;
  }
  const block = dxf.dxf.blocks[entity.block];
  if (!block?.entities?.length) {
    return null;
  }
  const { parsedEntities, blockTransform } = parseBlock(block, dxf);
  if (parsedEntities.length === 0) {
    return null;
  }
  const transformWithComponents = mat3TransformWithComponents(blockTransform);
  return parsedEntities.map((g) => sheetGeometryTransform(g, transformWithComponents));
}

function parseDimensionEntity(entity: IDimensionEntity, dxf: ExtendedDxf) {
  const fromBlock = parseDimensionBlock(entity, dxf);
  if (fromBlock) {
    return fromBlock;
  }

  const { linearOrAngularPoint1, linearOrAngularPoint2, anchorPoint } = entity;

  if (!linearOrAngularPoint1 || !linearOrAngularPoint2 || !anchorPoint) {
    return null;
  }

  if (entity.diameterOrRadiusPoint || entity.arcPoint) {
    //  TODO - arc/radius dimensions not supported at this time
    return null;
  }

  const { dimensionType } = parseDimensionType(entity.dimensionType ?? 0);
  // Only linear types map to Higharc linear dims
  if (
    dimensionType !== DXF_DIMENSION_TYPE.ROTATED_HORIZONTAL_VERTICAL &&
    dimensionType !== DXF_DIMENSION_TYPE.ALIGNED
  ) {
    return null;
  }

  const startX = linearOrAngularPoint2.x;
  const startY = linearOrAngularPoint2.y;

  const endX = linearOrAngularPoint1.x;
  const endY = linearOrAngularPoint1.y;

  const lineStartX = anchorPoint.x;
  const lineStartY = anchorPoint.y;

  // vector from the start of the dimensioned line to the anchor
  const [nx, ny] = line2Tangent(startX, startY, lineStartX, lineStartY);
  // vector from the start of the dimensioned line to the end of the dimensioned line
  const [tx, ty] = line2Tangent(startX, startY, endX, endY);

  let orientation: "ALIGNED" | "VERTICAL" | "HORIZONTAL" | null = null;
  const nDot = vec2Dot(nx, ny, tx, ty);
  const vDot = vec2Dot(nx, ny, 0, 1);
  if (Math.abs(nDot) < ANGLE_EPSILON) {
    orientation = "ALIGNED";
  } else if (Math.abs(vDot) < ANGLE_EPSILON) {
    orientation = "VERTICAL";
  } else {
    orientation = "HORIZONTAL";
  }

  if (!orientation) {
    return null;
  }

  let lineEndX = lineStartX;
  let lineEndY = lineStartY;

  switch (orientation) {
    case "ALIGNED": {
      const [dx, dy] = vec2Sub(lineStartX, lineStartY, startX, startY);
      [lineEndX, lineEndY] = vec2Add(endX, endY, dx, dy);
      break;
    }
    case "HORIZONTAL": {
      lineEndX = endX;
      break;
    }
    case "VERTICAL": {
      lineEndY = endY;
      break;
    }
  }

  const dimension = linearDimensionNew({
    positioningMethod: PositioningMethod.GLOBAL,
    startX,
    startY,
    lineStartX,
    lineStartY,
    endX,
    endY,
    lineEndX,
    lineEndY,
    orientation,
    witnessGap: 1,
    tickLength: 1,
    // Text height from DIMSTYLE / DSTYLE (DIMTXT) only — not from the anonymous *D block TEXT/MTEXT.
    fontSize: getDXFDefaultDimensionTextHeight(dxf, entity.styleName, entity.dstyleOverrides),
  });

  const dimensionGeom = sheetGeometryListNew();
  planAddAnnotation(dimensionGeom, {
    annotation: [dimension],
    assetCatalog: {},
  });

  return dimensionGeom.geometry;
}

function parseMTextEntity(entity: IMtextEntity) {
  const { attachmentPoint = 1, position, width, height = 1, rotation = 0, text } = entity;
  const { x, y } = position;

  const [placementXAnchor, placementYAnchor] = DXF_MTEXT_ANCHORS[attachmentPoint];
  const boxWidth = width ? sheetInchesNew(width) : undefined;

  const parsedText = parseDXFTextRTFOverrides(text);

  let alignment = TextAlignment.LEFT;
  if (placementXAnchor === PlacementXAnchor.CENTER) {
    alignment = TextAlignment.CENTER;
  } else if (placementXAnchor === PlacementXAnchor.RIGHT) {
    alignment = TextAlignment.RIGHT;
  }

  if (parsedText.alignment) {
    alignment = parsedText.alignment;
  }

  return sheetTextGeometryNew({
    x,
    y,
    text: parsedText.text,
    fontSize: sheetInchesNew(height),
    rotation: degreeToRadian(rotation),
    placementXAnchor,
    placementYAnchor,
    boxWidth,
    alignment,
    verticalAlignment: TextVerticalAlignment.TOP,
  });
}

function parseTextEntity(entity: ITextEntity) {
  const { halign = 0, valign = 0, startPoint, textHeight = 1, rotation = 0, text } = entity;
  const { x, y } = startPoint;

  const placementXAnchor = DXF_PLACEMENT_X_ANCHOR[halign];
  const placementYAnchor = DXF_PLACEMENT_Y_ANCHOR[valign];

  const parsedText = parseDXFTextOverrides(text);

  let alignment = TextAlignment.LEFT;
  if (placementXAnchor === PlacementXAnchor.CENTER) {
    alignment = TextAlignment.CENTER;
  } else if (placementXAnchor === PlacementXAnchor.RIGHT) {
    alignment = TextAlignment.RIGHT;
  }

  if (parsedText.alignment) {
    alignment = parsedText.alignment;
  }

  return sheetTextGeometryNew({
    x,
    y,
    text: parsedText.text,
    fontSize: sheetInchesNew(textHeight),
    rotation: degreeToRadian(rotation),
    placementXAnchor,
    placementYAnchor,
    alignment,
    verticalAlignment: TextVerticalAlignment.TOP,
  });
}

function parseAttribEntity(entity: IAttribEntity) {
  if (!entity.insertionPoint || !entity.text) {
    return null;
  }

  const { halign = 0, valign = 0, textRotation = 0, text, textHeight = 1, insertionPoint } = entity;
  let { x, y } = insertionPoint;

  const placementXAnchor = DXF_PLACEMENT_X_ANCHOR[halign];
  const placementYAnchor = DXF_PLACEMENT_Y_ANCHOR[valign];

  const parsedText = parseDXFTextOverrides(text);

  let alignment = TextAlignment.LEFT;
  if (placementXAnchor === PlacementXAnchor.CENTER) {
    alignment = TextAlignment.CENTER;
  } else if (placementXAnchor === PlacementXAnchor.RIGHT) {
    alignment = TextAlignment.RIGHT;
  }

  let verticalAlignment = TextVerticalAlignment.TOP;
  if (placementYAnchor === PlacementYAnchor.CENTER) {
    verticalAlignment = TextVerticalAlignment.CENTER;
  } else if (placementYAnchor === PlacementYAnchor.BOTTOM) {
    verticalAlignment = TextVerticalAlignment.BOTTOM;
  }

  if (parsedText.alignment) {
    alignment = parsedText.alignment;
  }

  if (alignment !== TextAlignment.LEFT && entity.anchorPoint) {
    x = entity.anchorPoint.x;
    y = entity.anchorPoint.y;
  }

  return sheetTextGeometryNew({
    x,
    y,
    text: parsedText.text,
    fontSize: sheetInchesNew(textHeight),
    rotation: degreeToRadian(textRotation),
    placementXAnchor,
    placementYAnchor,
    alignment,
    verticalAlignment,
  });
}

function parseLeaderEntity(entity: ILeaderEntity, dxf: ExtendedDxf) {
  if (!entity.vertices.length) {
    return null;
  }

  const pl = pointEntityArrayToPolyline2(entity.vertices);
  const x = pl[pl.length - 2];
  const y = pl[pl.length - 1];

  let landingLineX: number | undefined = undefined;
  let landingLineY: number | undefined = undefined;
  if (pl.length > 4) {
    landingLineX = pl[2];
    landingLineY = pl[3];
  }

  const leaderNote = noteNew({
    text: "__dummy__",
    x,
    y,
    leaderLineX: pl[0],
    leaderLineY: pl[1],
    landingLineX,
    landingLineY,
    leaderLineEndStyle: entity.arrowHead === false ? PathEndStyle.NONE : PathEndStyle.ARROW,
  });

  const leaderGeom = sheetGeometryListNew();
  planAddAnnotation(leaderGeom, {
    annotation: [leaderNote],
    annotationNoteLeaderArrowProps: getDXFDefaultLeaderArrowProps(dxf, entity.styleName, entity.dstyleOverrides),
    assetCatalog: {},
  });

  // remove __dummy__ text geometry from leader
  return leaderGeom.geometry.filter((g) => !isSheetTextGeometry(g));
}

function parseMLeaderEntity(entity: IMLeaderEntity, dxf: ExtendedDxf) {
  if (!entity.contextData) {
    return null;
  }

  const leaderGeom = sheetGeometryListNew();

  const { contextData } = entity;

  if (contextData.textContents && contextData.textLocation) {
    const {
      textContents,
      textLocation,
      textRotation = 0,
      textWidth,
      textHeight = 1,
      textAttachmentPoint = 1,
    } = contextData;

    const { x, y } = textLocation;
    const [placementXAnchor, placementYAnchor] = DXF_MTEXT_ANCHORS[textAttachmentPoint];
    const boxWidth = textWidth ? sheetInchesNew(textWidth) : undefined;

    const parsedText = parseDXFTextRTFOverrides(textContents);

    let alignment = TextAlignment.LEFT;
    if (placementXAnchor === PlacementXAnchor.CENTER) {
      alignment = TextAlignment.CENTER;
    } else if (placementXAnchor === PlacementXAnchor.RIGHT) {
      alignment = TextAlignment.RIGHT;
    }

    if (parsedText.alignment) {
      alignment = parsedText.alignment;
    }

    const textGeom = sheetTextGeometryNew({
      x,
      y,
      text: parsedText.text,
      fontSize: sheetInchesNew(textHeight),
      rotation: degreeToRadian(textRotation),
      placementXAnchor,
      placementYAnchor,
      boxWidth,
      alignment,
      verticalAlignment: TextVerticalAlignment.TOP,
    });

    leaderGeom.geometry.push(textGeom);
  }

  // Process each leader line
  for (const leader of contextData.leaders) {
    if (!leader.leaderLines.length) {
      continue;
    }

    for (const leaderLine of leader.leaderLines) {
      const vertices = [...leaderLine.vertices];

      if (leader.lastLeaderLinePoint) {
        vertices.push(leader.lastLeaderLinePoint);
      }

      const pl = pointEntityArrayToPolyline2(vertices);
      let x = pl[pl.length - 2];
      let y = pl[pl.length - 1];

      let landingLineX: number | undefined = undefined;
      let landingLineY: number | undefined = undefined;

      if (leader.doglegVector) {
        const doglegLength = leader.doglegLength ?? 0;
        const tx = x + leader.doglegVector.x * doglegLength;
        const ty = y + leader.doglegVector.y * doglegLength;
        landingLineX = x;
        landingLineY = y;
        x = tx;
        y = ty;
      }

      const leaderNote = noteNew({
        textBoxWidth: ZERO_INCHES,
        textBoxHeight: ZERO_INCHES,
        text: "__dummy__",
        x,
        y,
        leaderLineX: pl[0],
        leaderLineY: pl[1],
        landingLineX,
        landingLineY,
        leaderLineEndStyle: contextData.arrowHeadSize ? PathEndStyle.ARROW : PathEndStyle.NONE,
      });

      planAddAnnotation(leaderGeom, {
        annotation: [leaderNote],
        annotationNoteLeaderArrowProps: contextData.arrowHeadSize
          ? {
              width: contextData.arrowHeadSize / 2,
              length: contextData.arrowHeadSize,
            }
          : getDXFDefaultLeaderArrowProps(dxf),
        assetCatalog: {},
      });
    }
  }

  // remove __dummy__ text geometry from leader
  return leaderGeom.geometry.filter((g) => {
    if (isSheetTextGeometry(g)) {
      return g.text !== "__dummy__";
    }

    return true;
  });
}

function parseHatchEntity(entity: IHatchEntity) {
  const { boundaryPaths = [], patternName } = entity;

  const hatchKey = patternName as keyof typeof DXF_HATCH_PATTERNS;
  const hatchGeom = sheetGeometryListNew();

  for (const boundaryPath of boundaryPaths) {
    const { vertices } = boundaryPath;

    const linework: Linework[] = [];

    if (vertices) {
      let pl = pointEntityArrayToPolyline2(vertices);
      if (!polyLine2IsClosed(pl)) {
        pl = polyLine2Close(pl);
      }
      linework.push(
        lineworkNew({
          path: pl,
          // default to solid if we don't have a matching pattern
          fillType: DXF_HATCH_PATTERNS[hatchKey] ?? DXF_HATCH_PATTERNS.SOLID,
        }),
      );
    }

    planAddAnnotation(hatchGeom, {
      annotation: linework,
      assetCatalog: {},
    });
  }

  return hatchGeom.geometry;
}

function parseArcEntity(entity: IArcEntity): SheetArcGeometry | null {
  const { center, startAngle, endAngle, radius } = entity;
  if (!center || radius === undefined) {
    return null;
  }

  // Normalize angles to [0, 2π]
  const normalizeAngle = (angle: number) => ((angle % TWO_PI) + TWO_PI) % TWO_PI;
  const normalizedStartAngle = normalizeAngle(startAngle);
  const normalizedEndAngle = normalizeAngle(endAngle);

  // Compute the correct counterclockwise angle length
  let angleLength = normalizedEndAngle - normalizedStartAngle;
  if (angleLength < 0) {
    angleLength += TWO_PI;
  } // Ensure counterclockwise direction

  return sheetArcGeometryNew({
    x: center.x,
    y: center.y,
    xRadius: radius,
    yRadius: radius,
    startRad: normalizedStartAngle,
    endRad: normalizedStartAngle + angleLength,
    clockwise: false, // DXF arcs are always counterclockwise
  });
}

function parseCircleEntity(entity: ICircleEntity): SheetCircleGeometry | null {
  const { center, radius } = entity as any;

  return sheetCircleGeometryNew({
    x: center.x,
    y: center.y,
    radius,
  });
}

function parseEllipseEntity(entity: IEllipseEntity): SheetPolylineGeometry | null {
  const { center, majorAxisEndPoint, axisRatio, startAngle = 0, endAngle = Math.PI * 2 } = entity;
  if (!center || !majorAxisEndPoint || axisRatio === undefined) {
    return null;
  }

  const SEGMENTS = 32;
  const points: number[] = [];

  // majorAxisEndPoint is already a direction vector
  const majorLength = Math.sqrt(majorAxisEndPoint.x * majorAxisEndPoint.x + majorAxisEndPoint.y * majorAxisEndPoint.y);
  const majorAngle = Math.atan2(majorAxisEndPoint.y, majorAxisEndPoint.x);
  const minorLength = majorLength * axisRatio;

  // Use original angles to preserve multiple revolutions
  const segmentCount = Math.max(4, Math.floor((Math.abs(endAngle - startAngle) / (Math.PI * 2)) * SEGMENTS));

  for (let i = 0; i <= segmentCount; i++) {
    const t = i / segmentCount;
    const param = startAngle + (endAngle - startAngle) * t;
    const cosParam = Math.cos(param);
    const sinParam = Math.sin(param);

    // Parametric equation of the ellipse
    const x = center.x + majorLength * cosParam * Math.cos(majorAngle) - minorLength * sinParam * Math.sin(majorAngle);
    const y = center.y + majorLength * cosParam * Math.sin(majorAngle) + minorLength * sinParam * Math.cos(majorAngle);
    points.push(x, y);
  }

  // Close the shape if it's a full ellipse or has multiple revolutions
  const angleDelta = Math.abs(endAngle - startAngle);
  const isClosedEllipse = angleDelta >= Math.PI * 2 - ANGLE_EPSILON;

  if (isClosedEllipse) {
    points.push(points[0], points[1]);
  }

  return sheetPolylineGeometryNew({
    paths: [polyLine2New(points)],
  });
}

function parseSolidEntity(entity: ISolidEntity): SheetPolylineGeometry | null {
  const { firstCorner, secondCorner, thirdCorner, fourthCorner } = entity;
  if (!firstCorner || !secondCorner || !thirdCorner) {
    return null;
  }

  const corners = fourthCorner
    ? [firstCorner, secondCorner, fourthCorner, thirdCorner] // square
    : [firstCorner, secondCorner, thirdCorner]; // triangle
  const pts: number[] = [];
  for (const corner of corners) {
    if (
      pts.length >= 2 &&
      vec2DistSquared(pts[pts.length - 2], pts[pts.length - 1], corner.x, corner.y) < DIST_EPSILON_SQUARED
    ) {
      continue;
    }
    pts.push(corner.x, corner.y);
  }
  if (pts.length < 6) {
    return null;
  }

  return sheetPolylineGeometryNew({
    paths: [polyLine2Close(polyLine2New(pts))],
    fill: true,
    fillOpacity: percentNew(1),
    linewidth: ZERO_POINTS,
    strokeOpacity: percentNew(0),
  });
}

type LineworkVertex = IPoint & { bulge?: number };

/**
 * @TODO - This function is grouped for now because we don't currently need to do
 * anything different between these types
 * @param entity
 * @returns
 */
function parseLineworkEntity(entity: ILineEntity | ILwpolylineEntity | IPolylineEntity): SheetPolylineGeometry | null {
  if (!entity.vertices.length) {
    return null;
  }

  const isClosedPolyline = entity.type === "LINE" ? false : !!(entity as ILwpolylineEntity | IPolylineEntity).shape;

  const pl = pointEntityArrayToPolyline2(entity.vertices, isClosedPolyline);
  return sheetPolylineGeometryNew({
    paths: [pl],
  });
}

function pointEntityArrayToPolyline2(arr: LineworkVertex[], close = false): PolyLine2 {
  const pts: number[] = [];
  for (let i = 0; i < arr.length; i++) {
    pts.push(arr[i].x, arr[i].y);
    const bulge = arr[i].bulge;
    const isLast = i === arr.length - 1;
    if (bulge === undefined || bulge === 0 || (isLast && !close)) {
      continue;
    }
    const next = isLast ? arr[0] : arr[i + 1];
    for (const [x, y] of bulgeArcIntermediatePoints(arr[i].x, arr[i].y, next.x, next.y, bulge)) {
      pts.push(x, y);
    }
  }

  if (close) {
    pts.push(pts[0], pts[1]);
  }

  return polyLine2New(pts);
}

export function isInternalLineType(lineType: string) {
  const lineTypeUpper = lineType.toUpperCase();
  return lineTypeUpper === "BYLAYER" || lineTypeUpper === "BYBLOCK" || lineTypeUpper === "CONTINUOUS";
}

function getDXFDefaultLeaderArrowProps(
  dxf: ExtendedDxf,
  styleName?: string,
  dstyleOverrides?: DXFDimStyleOverrides,
): { width: number; length: number } {
  const length = resolveNormalizedDimSize({
    dimStyles: dxf.dimStyles,
    dimHeaderDefaults: dxf.dimHeaderDefaults,
    styleName,
    varKey: "dimAsz",
    fallbackExpectedSize: 2,
    dstyleOverrides,
  });
  if (length !== undefined) {
    return {
      width: length / 2,
      length,
    };
  }
  return {
    width: 1,
    length: 2,
  };
}

function getDXFDefaultDimensionTextHeight(
  dxf: ExtendedDxf,
  styleName: string | undefined,
  dstyleOverrides?: DXFDimStyleOverrides,
) {
  const height = resolveNormalizedDimSize({
    dimStyles: dxf.dimStyles,
    dimHeaderDefaults: dxf.dimHeaderDefaults,
    styleName,
    varKey: "dimTxt",
    fallbackExpectedSize: ONE_INCH,
    dstyleOverrides,
  });
  if (height !== undefined) {
    return sheetInchesNew(height);
  }
  return ONE_INCH;
}
